import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import {
  fontSize,
  fontWeight,
  lineHeight,
  maxHeight,
  maxWidth,
  space,
  width,
} from "styled-system";
import { selectClasses } from "./SelectV2.constants.ts";
import type { VirtualItem } from "@tanstack/react-virtual";

export const info = css`
  margin-left: 5px;
`;

export const floatingContainer = (theme: ExactTheme) => css`
  border-radius: ${theme.borderRadiusV2.primary};
  overflow: hidden;
  background-color: white;
  box-shadow: ${theme.shadowV2.secondary};
  outline: none;
  display: flex;
  flex-direction: column;
`;

export const arrowWrapper =
  ({
    isDisabled,
    truncateOptions = false,
    isButton,
  }: {
    isDisabled: boolean;
    truncateOptions?: boolean;
    isButton: boolean;
  }) =>
  ({ colorsV2 }: ExactTheme) => css`
    display: flex;
    margin-right: 10px;
    margin-left: 6px;

    cursor: ${isDisabled ? "not-allowed" : "pointer"};
    //transition: transform 0.25s ease-out;
    color: ${isDisabled
      ? colorsV2.grey[300]
      : isButton
      ? colorsV2.blue[500]
      : colorsV2.grey[800]};
  `;

export const arrow =
  ({ isOpen }: { isOpen: boolean }) =>
  (theme: ExactTheme) => css`
    transition: transform 0.3s ease;
    transform-origin: center;
    transform: ${`rotate(${isOpen ? 0 : 180}deg)`};
  `;

export const input = css`
  position: absolute;
  display: block;
  left: 0;
  width: 100%;
  top: 0;
  border: none;
  appearance: none;
  outline: none;
  background-color: transparent;
  visibility: hidden;
`;

export const previewInner = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const preview =
  ({
    disabled,
    isPlaceholder,
    isButton,
    isOpen,
  }: {
    disabled: boolean;
    isPlaceholder?: boolean;
    isButton: boolean;
    isOpen?: boolean;
  }) =>
  ({
    colorsV2,
    selectV2,
    inputV2Placeholder,
    inputColorV2,
    inputWeightV2,
  }: ExactTheme) => css`
    user-select: none;
    //transition: background-color 0.15s;
    display: flex;
    min-width: 0;
    flex-grow: 1;

    ${{
      ...selectV2,
      color: inputColorV2.normal,
      fontWeight: inputWeightV2.normal,
      ...(disabled
        ? {
            color: inputColorV2.disabled,
            fontWeight: inputWeightV2.disabled,
          }
        : {}),
    }};

    ${isPlaceholder
      ? isButton
        ? {
            fontWeight: inputWeightV2.normal,
          }
        : {
            ...inputV2Placeholder,
          }
      : {}}
    ${isButton &&
    isOpen && {
      color: colorsV2.white,
    }}

    cursor: ${disabled ? "not-allowed" : "pointer"};
  `;

export const root = ({ isButton }: { isButton: boolean }) => css`
  width: ${isButton ? "auto" : "100%"};
  position: relative;
`;

const outline = ({
  forceEnabled = false,
  hasError,
  theme: { colorsV2, inputBorderV2 },
  disabled,
  isButton,
}: {
  forceEnabled?: boolean;
  hasError: boolean;
  disabled: boolean;
  theme: ExactTheme;
  isButton: boolean;
}) => {
  const shadowStyle = hasError
    ? `0 0 0 1px ${colorsV2.red[500]}`
    : `0 0 0 1px ${colorsV2.blue[500]}`;

  if (isButton) {
    return {
      border: inputBorderV2.solid["focused"]["default"],
    };
  }

  if (forceEnabled) {
    return {
      border:
        inputBorderV2.solid[disabled ? "disabled" : "focused"][
          hasError ? "error" : "default"
        ],
      boxShadow: shadowStyle,
    };
  }

  return {
    boxShadow: "none",
    border:
      inputBorderV2.solid[disabled ? "disabled" : "standard"][
        hasError ? "error" : "default"
      ],
    transition: "border 150ms, box-shadow 150ms, color 150ms",

    "&:hover": !disabled
      ? {
          border: inputBorderV2.solid.hover[hasError ? "error" : "default"],
        }
      : {},
  };
};

export const chip =
  ({
    truncateOptions,
    disabled,
  }: {
    truncateOptions: boolean;
    disabled: boolean;
  }) =>
  ({ inputColorV2 }: ExactTheme) =>
    css({
      flexShrink: truncateOptions ? 0 : 1,
      color: disabled ? inputColorV2.disabled : inputColorV2.normal,
    });

export const multiSelectPreviewContainer =
  ({
    truncateOptions,
    disabled,
    hasValue,
  }: {
    truncateOptions?: boolean;
    disabled: boolean;
    hasValue: boolean;
  }) =>
  ({ inputWeightV2 }: ExactTheme) =>
    css({
      display: "flex",
      flexWrap: truncateOptions || !hasValue ? "nowrap" : "wrap",
      overflow: truncateOptions || !hasValue ? "hidden" : "hidden",
      gap: 5,
      marginRight: "auto",
      padding: "5px 11px",
      minWidth: "0px",
      flexGrow: 1,
      fontWeight: disabled ? inputWeightV2.disabled : inputWeightV2.normal,
    });

export const container =
  ({
    isOpen,
    hasError,
    isDisabled = false,
    isMultiple,
    isButton,
  }: {
    isOpen: boolean;
    hasError: boolean;
    isDisabled?: boolean;
    isMultiple: boolean;
    isButton: boolean;
  }) =>
  (theme: ExactTheme) => [
    css`
      display: flex;
      align-items: center;

      cursor: ${isDisabled ? "not-allowed" : "pointer"};

      font-size: ${theme.fontSizesV2.body};
      font-weight: ${theme.fontWeights.normal};
      :focus-visible {
        outline: none;
      }

      ${space({ theme })}
      ${width({ theme })}
      ${maxWidth({ theme })}
      ${maxHeight({ theme })}
      ${fontSize({ theme })}
      ${fontWeight({ theme })}
      ${lineHeight({ theme })}
        
      ${outline({
        forceEnabled: isOpen,
        hasError,
        theme,
        disabled: isDisabled,
        isButton,
      })};

      color: ${isDisabled
        ? theme.colorsV2.grey[500]
        : theme.colorsV2.grey[800]};
      border-radius: ${theme.borderRadiusV2.primary};
      background-color: ${isDisabled
        ? theme.colorsV2.grey[50]
        : hasError
        ? "white"
        : theme.colors.white};
    `,
    !isMultiple
      ? css`
          height: 37px;
        `
      : css`
          min-height: 37px;
        `,
    isButton &&
      css`
        transition: 150ms;
        .${selectClasses.startAdornment} {
          color: ${theme.colorsV2.blue[500]};
        }
        &:hover {
          background: ${theme.colorsV2.blue[500]};
          color: ${theme.colorsV2.white};
          .${selectClasses.arrow} {
            color: ${theme.colorsV2.white};
          }
          .${selectClasses.preview} {
            color: ${theme.colorsV2.white};
          }
          .${selectClasses.startAdornment} {
            color: ${theme.colorsV2.white};
          }
        }
        ${isOpen
          ? css`
              background: ${theme.colorsV2.blue[500]};
              color: ${theme.colorsV2.white};
              .${selectClasses.arrow} {
                color: ${theme.colorsV2.white};
              }
              .${selectClasses.startAdornment} {
                color: ${theme.colorsV2.white};
              }
            `
          : css``}
      `,
  ];

export const inner = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  min-width: 0;
`;

export const listItem =
  ({
    disabled,
    isActive,
    isSelected,
  }: {
    disabled?: boolean;
    isActive: boolean;
    isSelected: boolean;
  }) =>
  (theme: ExactTheme) => css`
    display: flex;
    user-select: none;
    flex-shrink: 0;
    //transition: background-color 0.15s;
    font-family: ${theme.fonts.normal};
    font-size: ${theme.fontSizesV2.body};
    & {
      color: ${disabled
        ? theme.inputColorV2.disabled
        : isSelected
        ? theme.colorsV2.grey[900]
        : theme.colorsV2.grey[800]};
      font-weight: ${isSelected
        ? theme.fontWeights.medium
        : theme.fontWeights.normal};
    }

    padding: 5px 10px;
    align-items: center;
    height: 35px;

    border-bottom: 1px solid ${theme.colorsV2.grey[100]};

    &:focus {
      outline: none;
    }

    &:hover {
      background-color: ${disabled
        ? "white"
        : !isSelected
        ? theme.colorsV2.grey[50]
        : theme.colorsV2.grey[50]};
    }
    &:active {
      background-color: ${disabled ? "white" : theme.colorsV2.grey[100]};
    }
    background-color: ${disabled
      ? "white"
      : isSelected
      ? theme.colorsV2.grey[50]
      : isActive
      ? theme.colorsV2.grey[50]
      : "transparent"};
    cursor: ${disabled ? "not-allowed" : "pointer"};
  `;

export const listItemInner = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const noResults = (theme: ExactTheme) => css`
  display: flex;
  user-select: none;
  //transition: background-color 0.15s;
  color: ${theme.colorsV2.grey[800]};
  padding: 10px;
  font-size: inherit;
  font-weight: inherit;
  border-bottom: 1px solid ${theme.colorsV2.grey[100]};
  background-color: transparent;
  position: relative;
`;

export const list = ({
  maxOptionsHeight,
}: {
  maxOptionsHeight?: number;
}) => css`
  overflow-y: auto;
  position: relative;
  max-height: ${maxOptionsHeight ? `${maxOptionsHeight}px` : "auto"};
`;

export const resetContainer = (theme: ExactTheme) => css`
  background: transparent;
  height: 33px;
  right: 0;
  display: flex;
  align-items: center;
  .${selectClasses.clearButton} {
    i {
      display: flex;
    }
  }
  &:hover {
    background-color: ${theme.colorsV2.grey[50]};
    .${selectClasses.clearButton} {
      padding: 0 6px;
      border-left: transparent;
      border-right: transparent;
    }
  }
  &:active {
    background-color: ${theme.colorsV2.grey[100]};
    .${selectClasses.clearButton} {
      padding: 0 6px;
      border-left: transparent;
      border-right: transparent;
    }
  }
`;

export const resetIconContainer = (theme: ExactTheme) => css`
  font-size: 15px;
  background-color: transparent;
  color: ${theme.colorsV2.grey[600]};

  align-items: center;

  border-left: 1px solid ${theme.colorsV2.grey[200]};
  border-right: 1px solid ${theme.colorsV2.grey[200]};

  padding: 0 5px;

  height: 15px;
`;

export const floatingOverlay = (theme: ExactTheme) => ({
  zIndex: theme.zIndices.dropdowns,
});

export const searchContainer = css`
  padding: 11px;
`;
export const searchInputContainer = css`
  width: 100%;
`;
export const placeholderText = (theme: ExactTheme) => css`
  ${theme.inputV2Placeholder}
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

export const groupLabel = (theme: ExactTheme) => css`
  font-size: ${theme.fontSizesV2.h6};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colorsV2.grey[900]};
  padding: 15px 0 0 10px;
  height: 35px;
`;

export const optionsContainer = ({ height }: { height: number }) => css`
  height: ${height}px;
  width: 100%;
  position: relative;
  outline: none;
`;

export const virtualOption = ({
  virtualItem,
}: {
  virtualItem: VirtualItem;
}) => css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${virtualItem.size}px;
  transform: translateY(${virtualItem.start}px);
`;

export const advancedSearchButton = (theme: ExactTheme) => css`
  min-width: 35px;
  min-height: 35px;
  color: ${theme.colorsV2.blue[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
`;
